import {
  addMonths,
  endOfMonth,
  isBefore,
  parseISO,
  startOfMonth,
} from "date-fns";

import { isValidDay } from "./day";

interface MonthWindow {
  start: Date;
  end: Date;
}

// The span of months a date picker opens on, seeded from a caller's day strings
// and falling back to this month through the next.
//
// Each end falls back on its own, and an unusable string falls back the same way
// an absent one does. That second part is the reason this exists: these strings
// come from the URL, where a truncated or hand-edited day parses to an invalid
// date. Seeding a picker with one used to throw while formatting it, on the
// first render and therefore past any local recovery — so a single stray
// character in a link cost the whole page rather than just the date in it.
// Opening on the current month instead loses only the caller's hint.
export function toMonthWindow(
  startDate?: string,
  endDate?: string
): MonthWindow {
  const start =
    startDate && isValidDay(startDate)
      ? startOfMonth(parseISO(startDate))
      : startOfMonth(new Date());
  const end =
    endDate && isValidDay(endDate)
      ? endOfMonth(parseISO(endDate))
      : endOfMonth(addMonths(new Date(), 1));

  // Falling back per end can put the today-anchored end before a caller-seeded
  // start, and a backwards window is worse than a defaulted one: availability
  // answers it with nothing, so every day reads as blocked and the picker goes
  // quietly unusable. Take the span from the start we settled on instead.
  if (isBefore(end, start)) {
    return { start, end: endOfMonth(addMonths(start, 1)) };
  }

  return { start, end };
}
